import { useMemo } from "react";
import { useLocation } from "react-router";

import { useMetricHelp } from "@/components/Analytics/useMetricHelp";
import { AskNobiButton } from "@/components/AskNobi/AskNobiButton";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { ChartLegend } from "@/components/Charts/ChartLegend";
import { dataColors } from "@/components/Charts/theme";
import { FilterFunnel01Icon } from "@/components/Icons/FilterFunnel01Icon";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { PageGrid } from "@/components/PageGrid/PageGrid";
import { Pagination } from "@/components/Pagination/Pagination";
import { addPortfolioProjectFilterQueryParams } from "@/components/ProjectFilter/portfolioProjectFilterQueryParams";
import { SearchInput } from "@/components/SearchInput/SearchInput";
import { Stat } from "@/components/Stat/Stat";
import { Button } from "@/design-system/Button";
import { Inline } from "@/design-system/Inline";
import { NumericBadge } from "@/design-system/NumericBadge";
import { SegmentGroup, type SegmentGroupItem } from "@/design-system/SegmentGroup";
import { Select } from "@/design-system/Select";
import { Spinner } from "@/design-system/Spinner";
import { Stack } from "@/design-system/Stack";
import type { DashboardTable } from "@/design-system/Table/helpers";
import { Table } from "@/design-system/Table/Table";
import { type TabItem, Tabs } from "@/design-system/Tabs";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { useSlug } from "@/hooks/useSlug";
import { useAnalyticsScope } from "@/modules/analytics/AnalyticsScopeContext";
import { AnalyticsCard } from "@/modules/analytics/components/AnalyticsCard";
import { AnalyticsPageContent } from "@/modules/analytics/components/AnalyticsPageContent";
import { AnalyticsSection } from "@/modules/analytics/components/AnalyticsSection";
import { BarChart } from "@/modules/analytics/components/BarChart";
import { LineChart } from "@/modules/analytics/components/LineChart";
import { ANALYTICS_AUDIENCE_SCOPE_COLORS, ANALYTICS_TABLE_PAGE_SIZE } from "@/modules/analytics/constants";
import type {
  EventAnalyticsEventRow,
  EventAnalyticsEventType,
  EventAnalyticsOccurrenceMode,
  EventAnalyticsResponse,
  EventAnalyticsSeries,
} from "@/queries/analytics/event-reporting-types";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";
import type { NonEmptyArray } from "@/types/utility-types";

import { hasProjectConnectionAudience } from "./audience-scopes";
import type { EventAnalyticsDateRangeOption } from "./EventAnalyticsPage";

// Palette used to distinguish recurring series on the multi-series line chart.
const SERIES_COLORS = [dataColors.primary, dataColors.green, dataColors.orange];

export interface EventAnalyticsContentProps {
  /** `focused` shows the core story; `all` adds every exploration section. Defaults to `all`. */
  mode?: "focused" | "all";
  data: EventAnalyticsResponse | undefined;
  isLoading: boolean;
  isFetching: boolean;
  selectedDateRange: EventAnalyticsDateRangeOption;
  dateRangeOptions: NonEmptyArray<EventAnalyticsDateRangeOption>;
  onDateRangeChange: (option: EventAnalyticsDateRangeOption) => void;
  selectedEventType: EventAnalyticsEventType;
  onEventTypeChange: (eventType: EventAnalyticsEventType) => void;
  occurrenceMode: EventAnalyticsOccurrenceMode;
  onOccurrenceModeChange: (mode: EventAnalyticsOccurrenceMode) => void;
  selectedSeriesId: string;
  onSeriesChange: (seriesId: string) => void;
  search: string;
  onSearchChange: (search: string) => void;
  activeFilterCount: number;
  onOpenFilters: () => void;
  eventTable: DashboardTable<EventAnalyticsEventRow>;
  seriesTable: DashboardTable<EventAnalyticsSeries>;
  page: number;
  pageCount: number;
  resultCount: number;
  onPageChange: (page: number) => void;
}

/**
 * Renders event analytics in either a concise `focused` view or the complete `all` view.
 * The complete view remains the default for existing consumers that do not specify a mode.
 */
export function EventAnalyticsContent({
  mode = "all",
  data,
  isLoading,
  isFetching,
  selectedDateRange,
  dateRangeOptions,
  onDateRangeChange,
  selectedEventType,
  onEventTypeChange,
  occurrenceMode,
  onOccurrenceModeChange,
  selectedSeriesId,
  onSeriesChange,
  search,
  onSearchChange,
  activeFilterCount,
  onOpenFilters,
  eventTable,
  seriesTable,
  page,
  pageCount,
  resultCount,
  onPageChange,
}: EventAnalyticsContentProps): React.ReactNode {
  const { t } = useTranslation();
  const analyticsScope = useAnalyticsScope();
  const location = useLocation();
  const slug = useSlug({ optional: true });
  const projectId = useProjectId();
  const portfolioRoute = (path: string) => addPortfolioProjectFilterQueryParams(path, location.search);
  const combinedFilterCount =
    activeFilterCount + (analyticsScope.type === "portfolio" ? analyticsScope.projectFilter.activeCount : 0);

  const tabItems = useMemo<TabItem<EventAnalyticsEventType>[]>(
    () => [
      {
        label: t("page.analytics-events.tabs.events"),
        value: "regular",
        count: data?.eventTypeCounts.regular,
        showZeroCount: true,
      },
      {
        label: t("page.analytics-events.tabs.announcements"),
        value: "announcement",
        count: data?.eventTypeCounts.announcement,
        showZeroCount: true,
      },
    ],
    [data?.eventTypeCounts, t],
  );

  return (
    <DocumentPage
      containerBoundary="inherit"
      tabTitle={mode === "focused" ? t("page.analytics-events.focus.title") : t("page.analytics-events.title")}
      title={mode === "focused" ? t("page.analytics-events.focus.title") : t("page.analytics-events.title")}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            {
              name: analyticsScope.type === "portfolio" ? t("page.portfolio.title") : t("page.analytics-story.title"),
              to:
                analyticsScope.type === "portfolio"
                  ? portfolioRoute(routes.portfolio.overview())
                  : routes.analytics.overview({ slug: slug! }),
            },
            {
              name: t("page.analytics-events.breadcrumb"),
            },
          ]}
        />
      }
      actions={
        <AskNobiButton
          defaultFilters={{ projectIds: analyticsScope.type === "portfolio" ? analyticsScope.projectIds : [projectId] }}
          context={{
            scope: analyticsScope.type,
            entity: "analytics",
            page: "detail",
          }}
          suggestions={[
            t("component.ask-nobi-chat-drawer.analytics.suggestion.1"),
            t("component.ask-nobi-chat-drawer.analytics.suggestion.2"),
          ]}
        />
      }
      header={
        <Inline gap="md" align="center">
          <Inline gap="sm" align="center" wrap={false}>
            <span className="text-body-emphasis">{t("page.analytics-details.time-range.label")}</span>
            <Select
              items={dateRangeOptions}
              selected={selectedDateRange}
              onChange={onDateRangeChange}
              renderOption={(option) => option.name}
              keySelector={(option) => option.value}
            />
          </Inline>
          <Button variant="soft" icon={<FilterFunnel01Icon />} onClick={onOpenFilters}>
            <Inline as="span" gap="sm" align="center" wrap={false}>
              {t("common.action.filter")}
              {combinedFilterCount > 0 && <NumericBadge count={combinedFilterCount} variant="filled" />}
            </Inline>
          </Button>
          {isFetching && !isLoading && <Spinner size="sm" />}
        </Inline>
      }
    >
      <AnalyticsPageContent>
        {mode === "focused" && (
          <p className="max-w-measure-wide text-body text-text-secondary">
            {t("page.analytics-events.focus.description")}
          </p>
        )}
        <Tabs.Root<EventAnalyticsEventType>
          tabs={tabItems}
          activeTab={selectedEventType}
          onSelectTab={onEventTypeChange}
        >
          <Tabs.Content value={selectedEventType}>
            {isLoading || !data ? (
              <section className="flex flex-1 items-center justify-center py-space-xl" aria-busy="true">
                <Spinner size="xl" />
              </section>
            ) : (
              <EventReportingContent
                mode={mode}
                data={data}
                selectedEventType={selectedEventType}
                occurrenceMode={occurrenceMode}
                onOccurrenceModeChange={onOccurrenceModeChange}
                selectedSeriesId={selectedSeriesId}
                onSeriesChange={onSeriesChange}
                search={search}
                onSearchChange={onSearchChange}
                activeFilterCount={combinedFilterCount}
                onOpenFilters={onOpenFilters}
                eventTable={eventTable}
                seriesTable={seriesTable}
                page={page}
                pageCount={pageCount}
                resultCount={resultCount}
                onPageChange={onPageChange}
              />
            )}
          </Tabs.Content>
        </Tabs.Root>
      </AnalyticsPageContent>
    </DocumentPage>
  );
}

interface EventReportingContentProps {
  mode: "focused" | "all";
  data: EventAnalyticsResponse;
  selectedEventType: EventAnalyticsEventType;
  occurrenceMode: EventAnalyticsOccurrenceMode;
  onOccurrenceModeChange: (mode: EventAnalyticsOccurrenceMode) => void;
  selectedSeriesId: string;
  onSeriesChange: (seriesId: string) => void;
  search: string;
  onSearchChange: (search: string) => void;
  activeFilterCount: number;
  onOpenFilters: () => void;
  eventTable: DashboardTable<EventAnalyticsEventRow>;
  seriesTable: DashboardTable<EventAnalyticsSeries>;
  page: number;
  pageCount: number;
  resultCount: number;
  onPageChange: (page: number) => void;
}

function EventReportingContent({
  mode,
  data,
  selectedEventType,
  occurrenceMode,
  onOccurrenceModeChange,
  selectedSeriesId,
  onSeriesChange,
  search,
  onSearchChange,
  activeFilterCount,
  onOpenFilters,
  eventTable,
  seriesTable,
  page,
  pageCount,
  resultCount,
  onPageChange,
}: EventReportingContentProps): React.ReactNode {
  const { t } = useTranslation();
  const help = useMetricHelp();
  const analyticsScope = useAnalyticsScope();
  const isPortfolio = analyticsScope.type === "portfolio";
  const { summary, recurring } = data;

  const isAnnouncement = selectedEventType === "announcement";
  const isRegular = selectedEventType === "regular";
  const hasRecurring = isRegular && (isPortfolio ? recurring.seriesCount > 0 : recurring.series.length > 0);
  const hasProjectConnections = hasProjectConnectionAudience(data.audienceScopes);

  const overviewCardTitle = getOverviewCardTitle(t, selectedEventType);
  const overviewCardTooltip = help(
    isAnnouncement
      ? "page.analytics-events.overview.announcements-title.tooltip"
      : "page.analytics-events.overview.regular-title.tooltip",
  );

  const stats = useMemo<{ label: string; value: number | undefined; renderFooter?: () => React.ReactNode }[]>(() => {
    const eventsStat = {
      label: isAnnouncement
        ? t("page.analytics-events.overview.announcements")
        : t("page.analytics-events.overview.events"),
      value: summary.occurrences,
      renderFooter: isAnnouncement
        ? undefined
        : () =>
            t("page.analytics-events.overview.one-off-recurring", {
              oneOff: summary.oneOffOccurrences,
              recurring: summary.recurringOccurrences,
            }),
    };
    const registrationsStat = {
      label: t("page.analytics-events.overview.registrations"),
      value: summary.registrations,
    };
    const uniqueParticipantsStat = {
      label: t("page.analytics-events.overview.unique-participants"),
      value: summary.uniqueParticipants,
    };
    const withoutRegistrationsStat = {
      label: t("page.analytics-events.overview.without-registrations"),
      value: summary.zeroRegistrationOccurrences,
    };
    if (isAnnouncement) {
      return [eventsStat];
    }

    return [eventsStat, registrationsStat, uniqueParticipantsStat, withoutRegistrationsStat];
  }, [isAnnouncement, summary, t]);

  const occurrenceOptions = useMemo<SegmentGroupItem<EventAnalyticsOccurrenceMode>[]>(
    () => [
      { value: "occurrences", label: t("page.analytics-events.toggle.occurrences") },
      { value: "series", label: t("page.analytics-events.toggle.series") },
    ],
    [t],
  );
  const selectedOccurrenceOption =
    occurrenceOptions.find((option) => option.value === occurrenceMode) ?? occurrenceOptions[0];
  const activityLabel = isAnnouncement
    ? t("page.analytics-events.activity.announcements-per-month")
    : t("page.analytics-events.activity.events-per-month");
  const activityAxisLabel = isAnnouncement
    ? t("page.analytics-events.chart.announcements-axis")
    : t("page.analytics-events.chart.events-axis");
  let audienceAxisLabel = t("page.analytics-events.toggle.occurrences");
  if (isAnnouncement) {
    audienceAxisLabel = t("page.analytics-events.chart.announcements-axis");
  } else if (occurrenceMode === "series") {
    audienceAxisLabel = t("page.analytics-events.toggle.series");
  }

  const eventBarData = useMemo(
    () =>
      data.monthly.map((point) => ({
        x: point.x,
        y: occurrenceMode === "series" ? point.series : point.occurrences,
        isPending: point.isCurrentMonth,
      })),
    [data.monthly, occurrenceMode],
  );

  const registrationLineData = useMemo(
    () => [
      {
        label: t("page.analytics-events.activity.registrations-per-month"),
        color: dataColors.green,
        dataPoints: data.monthly.map((point) => ({
          x: point.x,
          y: point.registrations,
          isPending: point.isCurrentMonth,
        })),
      },
    ],
    [data.monthly, t],
  );

  const audienceScopeLineData = useMemo(
    () =>
      data.audienceScopes.map((scope, index) => ({
        label:
          scope.type === "projectOnly"
            ? t("page.analytics-events.audience.project-only")
            : (scope.projectConnection?.name ?? t("page.analytics-events.filters.origin.external")),
        color: ANALYTICS_AUDIENCE_SCOPE_COLORS[index % ANALYTICS_AUDIENCE_SCOPE_COLORS.length],
        dataPoints: scope.dataPoints.map((point) => ({
          x: point.x,
          y: occurrenceMode === "series" ? point.series : point.occurrences,
          isPending: point.isCurrentMonth,
        })),
      })),
    [data.audienceScopes, occurrenceMode, t],
  );

  const organizerResidentData = useMemo(
    () =>
      data.organizerActivity.map((point) => ({
        x: point.x,
        y: point.residentLed,
        isPending: point.isCurrentMonth,
      })),
    [data.organizerActivity],
  );
  const organizerProjectTeamData = useMemo(
    () =>
      data.organizerActivity.map((point) => ({
        x: point.x,
        y: point.projectTeamLed,
        isPending: point.isCurrentMonth,
      })),
    [data.organizerActivity],
  );

  const seriesSelectItems = useMemo(
    () => [
      { id: "all", name: t("page.analytics-events.recurring.all-series") },
      ...recurring.series.map((series) => ({ id: series.id, name: series.name })),
    ],
    [recurring.series, t],
  );
  const selectedSeriesItem = seriesSelectItems.find((item) => item.id === selectedSeriesId) ?? seriesSelectItems[0];

  const shownSeries = useMemo(
    () =>
      selectedSeriesId === "all"
        ? recurring.series.slice(0, 3)
        : recurring.series.filter((series) => series.id === selectedSeriesId),
    [recurring.series, selectedSeriesId],
  );
  const seriesLineData = useMemo(
    () =>
      shownSeries.map((series, index) => ({
        label: series.name,
        color: SERIES_COLORS[index % SERIES_COLORS.length],
        dataPoints: series.dataPoints.map((point) => ({
          x: point.x,
          y: point.y,
          isPending: point.isCurrentMonth,
        })),
      })),
    [shownSeries],
  );

  const rangeFrom = resultCount === 0 ? 0 : page * ANALYTICS_TABLE_PAGE_SIZE + 1;
  const rangeTo = Math.min((page + 1) * ANALYTICS_TABLE_PAGE_SIZE, resultCount);

  const eventChartCard = (
    <AnalyticsCard.Root variant="graph" height="content">
      <AnalyticsCard.Header
        title={activityLabel}
        tooltip={help(
          isAnnouncement
            ? "page.analytics-events.activity.announcements-per-month.tooltip"
            : "page.analytics-events.activity.events-per-month.tooltip",
        )}
      >
        {!isAnnouncement && (
          <SegmentGroup<EventAnalyticsOccurrenceMode>
            value={occurrenceMode}
            items={occurrenceOptions}
            onChange={onOccurrenceModeChange}
          />
        )}
      </AnalyticsCard.Header>
      <figure className="flex min-h-scroll-area-compact w-full flex-1 flex-col">
        <BarChart
          dataPrimary={{ data: eventBarData }}
          xAxisType="date"
          yLabel={activityAxisLabel}
          yTickMode="integer"
          hoverLabel={(bar) => (
            <Stack>
              <span>{bar.primary?.y ?? 0}</span>
              {bar.primary?.isPending && (
                <span>{t("page.analytics-details.engagement.chart.tooltip.data-collection-in-progress")}</span>
              )}
            </Stack>
          )}
        />
        <ChartLegend
          items={[
            { label: isAnnouncement ? activityAxisLabel : selectedOccurrenceOption.label, color: dataColors.primary },
            {
              label: t("page.analytics-details.engagement.chart.legend.current-period"),
              color: dataColors.primary,
              pattern: "striped",
            },
          ]}
        />
      </figure>
    </AnalyticsCard.Root>
  );

  const registrationChartCard = (
    <AnalyticsCard.Root variant="graph" height="content">
      <AnalyticsCard.Header
        title={t("page.analytics-events.activity.registrations-per-month")}
        tooltip={help("page.analytics-events.activity.registrations-per-month.tooltip")}
      />
      <figure className="flex min-h-scroll-area-compact w-full flex-1 flex-col">
        <LineChart
          data={registrationLineData}
          xAxisType="date"
          yLabel={t("page.analytics-events.chart.registrations-axis")}
          hoverLabel={(point) => {
            const isPending = point.relatedPoints.some((related) => related.dataPoint.isPending);

            return (
              <ul className="flex flex-col gap-space-xs">
                {point.relatedPoints.map((related) => (
                  <li key={related.label}>
                    {related.label}: {related.dataPoint.y}
                  </li>
                ))}
                {isPending && (
                  <li>{t("page.analytics-details.engagement.chart.tooltip.data-collection-in-progress")}</li>
                )}
              </ul>
            );
          }}
        />
        <ChartLegend
          items={[
            {
              label: t("page.analytics-events.activity.registrations-per-month"),
              color: dataColors.green,
            },
          ]}
        />
      </figure>
    </AnalyticsCard.Root>
  );

  return (
    <PageGrid.Row>
      {/* Overview */}
      <PageGrid.Column size="100%">
        <AnalyticsSection
          isFullWidth={!isAnnouncement}
          title={
            mode === "focused"
              ? t("page.analytics-events.focus.signals")
              : t("page.analytics-events.overview.section-title")
          }
        >
          <AnalyticsCard.Root>
            <AnalyticsCard.Header title={overviewCardTitle} tooltip={overviewCardTooltip} />
            <section className="flex flex-col gap-space-md @xs:flex-row @xs:flex-wrap">
              {stats.map((stat) => (
                <Stat key={stat.label} label={stat.label} value={stat.value} renderFooter={stat.renderFooter} />
              ))}
            </section>
          </AnalyticsCard.Root>
        </AnalyticsSection>
      </PageGrid.Column>

      {/* Activity */}
      <PageGrid.Column size="100%">
        {isAnnouncement ? (
          <AnalyticsSection isFullWidth title={t("page.analytics-events.activity.section-title")}>
            {eventChartCard}
          </AnalyticsSection>
        ) : (
          <AnalyticsSection title={t("page.analytics-events.activity.section-title")}>
            {eventChartCard}
            {registrationChartCard}
          </AnalyticsSection>
        )}
      </PageGrid.Column>

      {/* Project connections, only for projects that have them */}
      {mode === "all" && hasProjectConnections && (
        <PageGrid.Column size="100%">
          <AnalyticsSection
            isFullWidth
            title={t("page.analytics-events.audience.section-title")}
            description={t("page.analytics-events.audience.description")}
          >
            <AnalyticsCard.Root variant="graph" height="content">
              <AnalyticsCard.Header
                title={
                  isAnnouncement
                    ? t("page.analytics-events.audience.announcements-title")
                    : t("page.analytics-events.audience.events-title")
                }
                tooltip={help(
                  isAnnouncement
                    ? "page.analytics-events.audience.announcements-title.tooltip"
                    : "page.analytics-events.audience.events-title.tooltip",
                )}
              >
                {!isAnnouncement && (
                  <SegmentGroup<EventAnalyticsOccurrenceMode>
                    value={occurrenceMode}
                    items={occurrenceOptions}
                    onChange={onOccurrenceModeChange}
                  />
                )}
              </AnalyticsCard.Header>
              <figure className="flex min-h-scroll-area-compact w-full flex-1 flex-col">
                <LineChart
                  data={audienceScopeLineData}
                  xAxisType="date"
                  yLabel={audienceAxisLabel}
                  hoverLabel={(point) => {
                    const isPending = point.relatedPoints.some((related) => related.dataPoint.isPending);

                    return (
                      <ul className="flex flex-col gap-space-xs">
                        {point.relatedPoints.map((related) => (
                          <li key={related.label}>
                            {related.label}: {related.dataPoint.y}
                          </li>
                        ))}
                        {isPending && (
                          <li>{t("page.analytics-details.engagement.chart.tooltip.data-collection-in-progress")}</li>
                        )}
                      </ul>
                    );
                  }}
                />
                <ChartLegend
                  items={audienceScopeLineData.map((series) => ({ label: series.label, color: series.color }))}
                />
              </figure>
            </AnalyticsCard.Root>
          </AnalyticsSection>
        </PageGrid.Column>
      )}

      {/* Resident initiative as a Social ESG indicator */}
      {mode === "all" && isRegular && data.organizerActivity.length > 0 && (
        <PageGrid.Column size="100%">
          <AnalyticsSection
            isFullWidth
            title={t("page.analytics-events.organizer.section-title")}
            description={t("page.analytics-events.organizer.description")}
          >
            <AnalyticsCard.Root variant="graph" height="content">
              <AnalyticsCard.Header
                title={t("page.analytics-events.organizer.chart-title")}
                tooltip={help("page.analytics-events.organizer.chart-title.tooltip")}
              />
              <figure className="flex min-h-scroll-area-compact w-full flex-1 flex-col">
                <BarChart
                  dataPrimary={{ data: organizerResidentData, color: dataColors.green }}
                  dataSecondary={{ data: organizerProjectTeamData, color: dataColors.primary }}
                  xAxisType="date"
                  yLabel={t("page.analytics-events.chart.events-axis")}
                  yTickMode="integer"
                  hoverLabel={(bar) => {
                    const isPending = bar.primary?.isPending || bar.secondary?.isPending;

                    return (
                      <Stack gap="xs">
                        <span>
                          {t("page.analytics-events.organizer.resident-led")}: {bar.primary?.y ?? 0}
                        </span>
                        <span>
                          {t("page.analytics-events.organizer.project-team-led")}: {bar.secondary?.y ?? 0}
                        </span>
                        {isPending && (
                          <span>
                            {t("page.analytics-details.engagement.chart.tooltip.data-collection-in-progress")}
                          </span>
                        )}
                      </Stack>
                    );
                  }}
                />
                <ChartLegend
                  items={[
                    {
                      label: t("page.analytics-events.organizer.resident-led"),
                      color: dataColors.green,
                    },
                    {
                      label: t("page.analytics-events.organizer.project-team-led"),
                      color: dataColors.primary,
                    },
                    {
                      label: t("page.analytics-details.engagement.chart.legend.current-period"),
                      color: dataColors.primary,
                      pattern: "striped",
                    },
                  ]}
                />
              </figure>
            </AnalyticsCard.Root>
          </AnalyticsSection>
        </PageGrid.Column>
      )}

      {/* Registration size + lead time (regular only) */}
      {mode === "all" && isRegular && (
        <PageGrid.Column size="100%">
          <section className="grid grid-cols-1 gap-space-lg @md:grid-cols-2">
            <AnalyticsCard.Root variant="graph" height="content">
              <AnalyticsCard.Header
                title={t("page.analytics-events.registration-size.title")}
                tooltip={help("page.analytics-events.registration-size.title.tooltip")}
              />
              <figure className="flex min-h-scroll-area-compact w-full flex-1 flex-col">
                <BarChart
                  dataPrimary={{ data: data.registrationSizes }}
                  yTickMode="integer"
                  hoverLabel={(bar) => <span>{bar.primary?.y ?? 0}</span>}
                />
              </figure>
            </AnalyticsCard.Root>
            <AnalyticsCard.Root variant="graph" height="content">
              <AnalyticsCard.Header
                title={t("page.analytics-events.registration-lead-time.title")}
                tooltip={help("page.analytics-events.registration-lead-time.title.tooltip")}
              />
              <figure className="flex min-h-scroll-area-compact w-full flex-1 flex-col">
                <BarChart
                  dataPrimary={{ data: data.registrationLeadTimes }}
                  yTickMode="integer"
                  hoverLabel={(bar) => <span>{bar.primary?.y ?? 0}</span>}
                />
              </figure>
            </AnalyticsCard.Root>
          </section>
        </PageGrid.Column>
      )}

      {/* Capacity (regular only) */}
      {mode === "all" && isRegular && (
        <PageGrid.Column size="100%">
          <AnalyticsSection
            isFullWidth
            title={t("page.analytics-events.capacity.section-title")}
            description={t("page.analytics-events.capacity.description", {
              count: data.capacity.cappedOccurrences,
              total: summary.occurrences,
            })}
          >
            <AnalyticsCard.Root variant="graph" height="content">
              <AnalyticsCard.Header
                title={t("page.analytics-events.capacity.card-title")}
                tooltip={help("page.analytics-events.capacity.card-title.tooltip")}
              />
              <figure className="flex min-h-scroll-area-compact w-full flex-1 flex-col">
                <BarChart
                  dataPrimary={{ data: data.capacity.distribution }}
                  yTickMode="integer"
                  hoverLabel={(bar) => <span>{bar.primary?.y ?? 0}</span>}
                />
              </figure>
            </AnalyticsCard.Root>
          </AnalyticsSection>
        </PageGrid.Column>
      )}

      {/* Recurring events (regular only, when data exists) */}
      {mode === "all" && hasRecurring && (
        <PageGrid.Column size="100%">
          <AnalyticsSection isFullWidth title={t("page.analytics-events.recurring.section-title")}>
            <section className="flex w-full flex-col gap-space-lg">
              <AnalyticsCard.Root variant={isPortfolio ? undefined : "graph"} height="content">
                <AnalyticsCard.Header
                  title={t("page.analytics-events.recurring.overview-title")}
                  tooltip={help("page.analytics-events.recurring.overview-title.tooltip")}
                >
                  {!isPortfolio && (
                    <Select
                      items={seriesSelectItems}
                      selected={selectedSeriesItem}
                      onChange={(item) => onSeriesChange(item.id)}
                      renderOption={(item) => item.name}
                      keySelector={(item) => item.id}
                    />
                  )}
                </AnalyticsCard.Header>
                <section className="grid grid-cols-1 gap-space-md @sm:grid-cols-3">
                  <Stat label={t("page.analytics-events.recurring.series")} value={recurring.seriesCount} />
                  <Stat label={t("page.analytics-events.recurring.occurrences")} value={recurring.occurrences} />
                  <Stat label={t("page.analytics-events.recurring.registrations")} value={recurring.registrations} />
                </section>
                {!isPortfolio && (
                  <figure className="flex min-h-scroll-area-compact w-full flex-1 flex-col gap-space-sm">
                    <span className="text-body text-text-secondary">
                      {t("page.analytics-events.recurring.chart-title")}
                    </span>
                    <LineChart
                      data={seriesLineData}
                      xAxisType="date"
                      hoverLabel={(point) => {
                        const isPending = point.relatedPoints.some((related) => related.dataPoint.isPending);

                        return (
                          <ul className="flex flex-col gap-space-xs">
                            {point.relatedPoints.map((related) => (
                              <li key={related.label}>
                                {related.label}: {related.dataPoint.y}
                              </li>
                            ))}
                            {isPending && (
                              <li>
                                {t("page.analytics-details.engagement.chart.tooltip.data-collection-in-progress")}
                              </li>
                            )}
                          </ul>
                        );
                      }}
                    />
                    <ChartLegend
                      items={seriesLineData.map((series) => ({ label: series.label, color: series.color }))}
                    />
                  </figure>
                )}
              </AnalyticsCard.Root>
              {!isPortfolio && (
                <AnalyticsCard.Root horizontalScroll>
                  <AnalyticsCard.Header title={t("page.analytics-events.recurring.table-title")} />
                  <Table table={seriesTable} data-testid="analytics-event-series-table" />
                </AnalyticsCard.Root>
              )}
            </section>
          </AnalyticsSection>
        </PageGrid.Column>
      )}

      {/* All events */}
      {mode === "all" && !isPortfolio && (
        <PageGrid.Column size="100%">
          <AnalyticsSection
            isFullWidth
            title={
              isAnnouncement
                ? t("page.analytics-events.table.announcements-section-title")
                : t("page.analytics-events.table.section-title")
            }
          >
            <AnalyticsCard.Root horizontalScroll>
              <AnalyticsCard.Header
                title={
                  isAnnouncement
                    ? t("page.analytics-events.table.announcements-title")
                    : t("page.analytics-events.table.title")
                }
              >
                <div className="flex w-full flex-col gap-space-md @sm:w-auto @sm:flex-row @sm:items-center">
                  <SearchInput
                    value={search}
                    onChange={(event) => onSearchChange(event.target.value)}
                    placeholder={
                      isAnnouncement
                        ? t("page.analytics-events.table.search-announcement")
                        : t("page.analytics-events.table.search")
                    }
                    aria-label={
                      isAnnouncement
                        ? t("page.analytics-events.table.search-announcement")
                        : t("page.analytics-events.table.search")
                    }
                    width="compact"
                  />
                  <Button variant="soft" icon={<FilterFunnel01Icon />} onClick={onOpenFilters}>
                    <Inline gap="sm" align="center" wrap={false}>
                      {t("common.action.filter")}
                      {activeFilterCount > 0 && <NumericBadge count={activeFilterCount} variant="filled" />}
                    </Inline>
                  </Button>
                </div>
              </AnalyticsCard.Header>
              <Table table={eventTable} data-testid="analytics-events-table">
                {resultCount > 0 && (
                  <Inline gap="md" align="center" justify="between">
                    <span className="text-caption text-text-secondary">
                      {t("page.analytics-events.table.results", {
                        from: rangeFrom,
                        to: rangeTo,
                        total: resultCount,
                      })}
                    </span>
                    {pageCount > 1 && <Pagination count={pageCount} currentIndex={page} onChange={onPageChange} />}
                  </Inline>
                )}
              </Table>
            </AnalyticsCard.Root>
          </AnalyticsSection>
        </PageGrid.Column>
      )}
    </PageGrid.Row>
  );
}

function getOverviewCardTitle(t: ReturnType<typeof useTranslation>["t"], eventType: EventAnalyticsEventType): string {
  if (eventType === "announcement") {
    return t("page.analytics-events.overview.announcements-title");
  }

  return t("page.analytics-events.overview.regular-title");
}
